import React, { ReactNode } from 'react';
import { useFormikContext, FormikContextType } from 'formik';
import { styled } from 'dripsy';
import { View } from 'react-native';

import { TextInput, ITextInputProps } from '../common/TextInput';
import { ErrorText } from './ErrorText';

const Container = styled(View)({});

const defaultValueTransformer = (v?: string): string | undefined => v;

export interface IFieldProps<T>
  extends Omit<ITextInputProps, 'onChangeText'> {
  name: string;
  valueTransformer?: (value?: string) => string | undefined;
  onChangeText?: (formik: FormikContextType<T>, text: string) => void;
}

export function Field<T extends Record<string, any>>({
  name,
  valueTransformer = defaultValueTransformer,
  onChangeText,
  onLayout,
  ...props
}: IFieldProps<T>) {
  const formik = useFormikContext<T>();

  function onChange(text: string) {
    // Transform typed value
    formik.setFieldValue(name, valueTransformer(text));
  }

  function onFocus() {}

  function onBlur() {
    formik.handleBlur(name);
    formik.setFieldTouched(name);
  }

  // Transform initial value
  const value = valueTransformer(formik.values[name]);

  const error = formik.errors[name];

  const isError = !!(
    typeof error !== 'undefined' && formik.touched[name]
  );

  return (
    <Container onLayout={onLayout}>
      <TextInput
        value={value}
        onFocus={onFocus}
        onBlur={onBlur}
        isError={isError}
        onChangeText={
          onChangeText
            ? (text) => onChangeText(formik, text)
            : onChange
        }
        {...props}
      />
      {isError && <ErrorText>{error as ReactNode}</ErrorText>}
    </Container>
  );
}
